import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/components/ui/use-toast';
import { useCart } from '@/contexts/CartContext';
import CreditCard from 'lucide-react/dist/esm/icons/credit-card.js';
import Smartphone from 'lucide-react/dist/esm/icons/smartphone.js';
import { useNavigate } from 'react-router-dom';
import { composeMockupImage } from '@/lib/composeMockupImage';
import { composeWorksheetImage } from '@/lib/composeWorksheetImage';
import { uploadCheckoutFilesToCloudinary } from '@/lib/cloudinaryCheckoutUploads';
import {
  WorksheetArtifactError,
  generateRequiredWorksheetPdf,
  assertWorksheetCloudinaryUploads,
  assertWorksheetPersistence,
} from '@/lib/worksheetArtifacts';
import { DEFAULT_DISCOUNT_RATE } from '@/lib/discountHelpers';
import { priceCart } from '@/lib/couponPricing';
import { buildCartItemsForAirtable } from '@/lib/buildCartItemsForAirtable';
import { useActionLogger } from '@/hooks/useActionLogger';
import {
  getGoogleAdsSnapshot,
  getTrackingMetadataSnapshot,
  buildGa4PurchaseItems,
  readGoogleAnalyticsIdentifiers,
} from '@/lib/googleAdsTracking';
import {
  normalizeCheckoutEpisodeId,
  resolveCheckoutEpisodeId,
  trackGa4CheckoutEvent,
} from '@/lib/checkoutTelemetry';
import {
  normalizeOrderIdempotencyKey,
  resolveOrderIdempotencyKey,
  persistFreshCheckoutSession,
} from '@/lib/orderSessionIdentity';
import { resolveOpsProductRefs } from '@/lib/opsProductNaming';
import { getOpenAIAdsContext } from '@/lib/openaiAdsTracking';
import { trackOpenAIAdsCheckout } from '@/lib/openaiAdsTelemetry';
import { products, printAreas } from '@/data/products';
import { EMBO_DEV_FEE, EMBO_UNIT_FEE } from '@/lib/pricingConstants';
import { getProductSideImage } from '@/lib/productImages';
import { buildIcountComplogCheckoutUrl } from '@/lib/icountComplogCheckout';
import { collectionProviderFromReadiness, validatedTakbullCheckoutUrl, preparePaymentCheckout } from '@/lib/paymentRouting';
import { buildCheckoutRetryIntent } from '@/lib/checkoutRetryIntent';
import { checkoutFetch, withCheckoutDeadline } from '@/lib/checkoutDeadline';
import { queueSubmittedBitPurchase, savePurchaseContext } from '@/lib/purchaseDataLayer';
import {
  evaluateCheckoutReadiness,
  getCheckoutReadinessMessage,
} from '@/lib/checkoutReadiness';

const methodCards = [
  { id: 'card', label: 'כרטיס אשראי (הנחה נוספת 3%)', icon: CreditCard },
  { id: 'bit', label: 'Bit / Paybox', icon: Smartphone },
];

const sumSizeMatrixQuantities = (matrix = {}) => Object.values(matrix || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);

const computeItemQuantity = (item) => {
  if (!item) return 0;
  if (item.sizeMatrices && typeof item.sizeMatrices === 'object') {
    return Object.values(item.sizeMatrices).reduce((sum, matrix) => sum + sumSizeMatrixQuantities(matrix), 0);
  }
  if (item.sizeMatrix && typeof item.sizeMatrix === 'object') {
    return sumSizeMatrixQuantities(item.sizeMatrix);
  }
  if (typeof item.quantity === 'number') return item.quantity;
  return 0;
};

const normalizeItemColors = (item) => {
  if (Array.isArray(item?.selectedColors) && item.selectedColors.length) return item.selectedColors;
  if (item?.color) return [item.color];
  return [];
};

const normalizePrintAreas = (item) => (item?.selectedPrintAreas || [])
  .map((entry) => {
    if (!entry) return null;
    if (typeof entry === 'string') return entry;
    return entry.areaKey || null;
  })
  .filter(Boolean);

const computePrintAreaFees = (item, quantity) => {
  const qty = Number(quantity) || 0;
  if (!item || qty <= 0) return [];
  const selections = Array.isArray(item.selectedPrintAreas) ? item.selectedPrintAreas : [];
  const breakdown = [];
  let hasEmbo = false;
  selections.forEach((entry) => {
    const areaKey = typeof entry === 'string' ? entry : (entry?.areaKey || '');
    if (!areaKey) return;
    const method = typeof entry === 'object' && entry?.method ? entry.method : 'print';
    if (method === 'embo') hasEmbo = true;
    let perUnitFee = 0;
    if (method === 'print') {
      perUnitFee = Number(printAreas[areaKey]?.fee || 0);
    } else if (method === 'embo') {
      perUnitFee = EMBO_UNIT_FEE;
    }
    if (!Number.isFinite(perUnitFee) || perUnitFee <= 0) return;
    const total = Number((perUnitFee * qty).toFixed(2));
    breakdown.push({
      type: 'print_area',
      areaKey,
      method,
      label: printAreas[areaKey]?.label || areaKey,
      labelHe: printAreas[areaKey]?.labelHe || '',
      quantity: qty,
      unit_fee: perUnitFee,
      total,
    });
  });
  if (hasEmbo) {
    breakdown.push({
      type: 'embo_setup',
      areaKey: 'embo_setup',
      method: 'embo',
      label: 'Embo setup fee',
      labelHe: 'דמי גלופת אמבו',
      quantity: 1,
      unit_fee: EMBO_DEV_FEE,
      total: EMBO_DEV_FEE,
    });
  }
  return breakdown;
};

const formatBreakdownLine = (label, quantity, unitPrice, totalPrice, formatCurrencyFn) => {
  if (!label || typeof formatCurrencyFn !== 'function') return '';
  const qty = Number(quantity);
  const qtyText = Number.isFinite(qty) && qty > 0 ? `${qty}pcs` : '';
  const unit = Number(unitPrice);
  const total = Number(totalPrice);
  const unitText = Number.isFinite(unit) ? formatCurrencyFn(unit) : formatCurrencyFn(0);
  const totalText = Number.isFinite(total)
    ? formatCurrencyFn(total)
    : (Number.isFinite(unit) && Number.isFinite(qty) ? formatCurrencyFn(unit * qty) : formatCurrencyFn(0));
  const parts = [label.trim()];
  if (qtyText) parts.push(qtyText);
  parts.push('x', unitText, '=', totalText);
  return parts.join(' ').replace(/\s+/g, ' ').trim();
};
// Lazy import for PDF generation to avoid bundle bloat when not needed
let pdfLibPromise = null;
async function ensurePdfLib() {
  if (!pdfLibPromise) {
    pdfLibPromise = import('pdf-lib');
  }
  return pdfLibPromise;
}
async function pngsToPdf(pngDataUrls, opts = {}) {
  return withCheckoutDeadline(async () => {
    const { widthPx = 1240, heightPx = 1754 } = opts;
    const { PDFDocument } = await ensurePdfLib();
    const pdfDoc = await PDFDocument.create();
    for (const dataUrl of pngDataUrls) {
      if (!dataUrl) continue;
      const res = await checkoutFetch(dataUrl);
      const pngBytes = await res.arrayBuffer();
      const image = await pdfDoc.embedPng(pngBytes);
      const page = pdfDoc.addPage([widthPx, heightPx]);
      page.drawImage(image, { x: 0, y: 0, width: widthPx, height: heightPx });
    }
    // Use pdf-lib's helper to produce a proper data URI to avoid manual btoa issues with large payloads
    const dataUri = await pdfDoc.saveAsBase64({ dataUri: true });
    return dataUri;
  }, { timeoutMs: 60000 });
}

export default function CheckoutModal({ open, onClose, cartSummary, prefillContact }) {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const { mergePayload, setPayload, payload, cartItems, getTotalItems } = useCart();
  const navigate = useNavigate();
  const { record, flushLogsToAirtable, clearLogs, getFormattedLog } = useActionLogger();
  const [method, setMethod] = useState('card');
  const [name, setName] = useState(prefillContact?.name || '');
  const [phone, setPhone] = useState(prefillContact?.phone || '');
  const [email, setEmail] = useState(prefillContact?.email || '');
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('מכין את התשלום…');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const discountClaimed = (() => {
    if (payload?.discountClaimed) return true;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        return window.sessionStorage.getItem('printee:discount-claimed') === 'true';
      }
    } catch (err) {
      // ignore storage access failures
    }
    return false;
  })();
  // Keep a reference to the single background forward promise so we can await it before navigating to iCount
  const forwardPromiseRef = useRef(null);
  const confirmInFlightRef = useRef(false);
  const idempotencyKeyRef = useRef(
    resolveOrderIdempotencyKey(payload?.idempotency_key).idempotencyKey,
  );
  const checkoutEpisodeIdRef = useRef(normalizeCheckoutEpisodeId(payload?.checkout_episode_id));
  const airtableRecordIdRef = useRef((payload && payload.airtable_record_id) || '');
  const dialogRef = useRef(null);
  const previouslyFocused = useRef(null);
  const loggedOpenRef = useRef(false);

  useEffect(() => {
    airtableRecordIdRef.current = payload?.airtable_record_id || '';
  }, [payload?.airtable_record_id]);

  const currencyFormatter = useMemo(() => {
    const locale = language === 'he' ? 'he-IL' : 'en-IL';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'ILS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      return new Intl.NumberFormat('en-IL', {
        style: 'currency',
        currency: 'ILS',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
  }, [language]);

  const formatCurrency = useCallback((value) => {
    const num = Number(value);
    const safe = Number.isFinite(num) ? num : 0;
    try {
      return currencyFormatter.format(safe);
    } catch {
      return `₪${safe.toFixed(2)}`;
    }
  }, [currencyFormatter]);

  const cartItemCount = Array.isArray(cartItems) ? cartItems.length : 0;
  const googleAdsSnapshot = getGoogleAdsSnapshot(payload?.tracking?.googleAds);
  const metadataSnapshot = getTrackingMetadataSnapshot(payload?.tracking?.metadata);
  const existingConversionTime = payload?.tracking?.metadata?.conversionTime || '';
  const snapshotConversionTime = metadataSnapshot.conversionTime || '';
  const checkoutAnalyticsItems = useMemo(() => buildGa4PurchaseItems((cartItems || []).map((item) => {
    const { productSku, productName } = resolveOpsProductRefs(item);
    return {
      itemId: productSku,
      itemName: productName,
      sizeMatrices: (item?.sizeMatrices && typeof item.sizeMatrices === 'object')
        ? item.sizeMatrices
        : (item?.color ? { [item.color]: (item.sizeMatrix || {}) } : {}),
      colors: normalizeItemColors(item),
      quantity: computeItemQuantity(item),
    };
  })), [cartItems]);
  const checkoutUnitCount = useMemo(
    () => (cartItems || []).reduce((sum, item) => sum + computeItemQuantity(item), 0),
    [cartItems],
  );
  const ga4MeasurementId = (import.meta?.env?.VITE_GA4_MEASUREMENT_ID || 'G-43KTYPJPNM').trim();

  const ensureCheckoutEpisodeId = useCallback(() => {
    const storedEpisodeId = normalizeCheckoutEpisodeId(payload?.checkout_episode_id);
    const refEpisodeId = normalizeCheckoutEpisodeId(checkoutEpisodeIdRef.current);
    const { episodeId, created } = resolveCheckoutEpisodeId(storedEpisodeId || refEpisodeId);
    checkoutEpisodeIdRef.current = episodeId;
    if (created || storedEpisodeId !== episodeId) {
      try { mergePayload({ checkout_episode_id: episodeId }); } catch (_) {}
    }
    return episodeId;
  }, [mergePayload, payload?.checkout_episode_id]);

  useEffect(() => {
    if (open && !loggedOpenRef.current) {
      loggedOpenRef.current = true;
      const checkoutEpisodeId = ensureCheckoutEpisodeId();
      try {
        record('checkout_modal_open', {
          checkoutEpisodeId,
          method,
          cartItems: cartItemCount,
          subtotal: Number(cartSummary?.total ?? 0),
        });
      } catch (_) {}
      trackGa4CheckoutEvent({
        eventName: 'begin_checkout',
        episodeId: checkoutEpisodeId,
        value: Number(cartSummary?.total ?? 0),
        cartItemCount,
        cartUnitCount: checkoutUnitCount,
        items: checkoutAnalyticsItems,
        measurementId: ga4MeasurementId,
      });
      trackOpenAIAdsCheckout({
        items: cartItems,
        episodeId: checkoutEpisodeId,
        amount: cartSummary?.total,
      });
    } else if (!open) {
      loggedOpenRef.current = false;
    }
  }, [
    open,
    record,
    method,
    cartItemCount,
    cartItems,
    cartSummary?.total,
    checkoutAnalyticsItems,
    checkoutUnitCount,
    ensureCheckoutEpisodeId,
    ga4MeasurementId,
  ]);

  const handlePaymentMethodChange = useCallback((nextId) => {
    if (!methodCards.some(({ id }) => id === nextId)) return;
    setMethod((prev) => {
      if (prev === nextId) return prev;
      try {
        record('checkout_select_payment_method', {
          previous: prev,
          next: nextId,
        });
      } catch (_) {}
      return nextId;
    });
  }, [record]);

  // Focus management: when modal opens, save previously focused element and move
  // focus into the dialog. When it closes, restore focus.
  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    // Move focus into the dialog container after render
    const timer = setTimeout(() => {
      try {
        // Prefer the first focusable element inside the dialog
        const root = dialogRef.current;
        if (!root) return;
        const focusable = root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (focusable && focusable.length) {
          (focusable[0]).focus();
        } else {
          root.focus();
        }
      } catch (e) { /* ignore */ }
    }, 0);

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        try { onClose(); } catch (_) {}
      }
      // Simple focus trap: if Tab pressed, ensure focus remains inside dialog
      if (e.key === 'Tab') {
        const root = dialogRef.current;
        if (!root) return;
        const focusable = Array.from(root.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
          .filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
        if (focusable.length === 0) {
          e.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        } else if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    document.addEventListener('keydown', onKey);
    const appRoot = document.querySelector('#root');
    const dialogLivesOutsideApp = appRoot && dialogRef.current ? !appRoot.contains(dialogRef.current) : true;
    const prevAria = appRoot?.getAttribute ? appRoot.getAttribute('aria-hidden') : null;
    const prevInert = appRoot ? appRoot.inert : false;
    const prevBodyOverflow = document.body.style.overflow;
    const activeElement = document.activeElement;
    try { document.body.style.overflow = 'hidden'; } catch (e) {}
    if (appRoot && dialogLivesOutsideApp) {
      try {
        if (activeElement && appRoot.contains(activeElement) && activeElement.blur) activeElement.blur();
      } catch (e) {}
      try { appRoot.setAttribute('aria-hidden', 'true'); } catch (e) {}
      try { appRoot.inert = true; } catch (e) {}
    }

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
      try {
        if (previouslyFocused.current && previouslyFocused.current.focus) previouslyFocused.current.focus();
      } catch (e) {}
      try {
        document.body.style.overflow = prevBodyOverflow || '';
      } catch (e) {}
      try {
        if (appRoot && dialogLivesOutsideApp) {
          appRoot.inert = prevInert;
          if (prevAria === null) appRoot.removeAttribute('aria-hidden');
          else appRoot.setAttribute('aria-hidden', prevAria);
        }
      } catch (e) {}
    };
  }, [open]);

  // Backend removed: no forwarding; only local state and client-side UX remain.

  useEffect(() => {
    if (!open) return;

    // Preserve one payment identity across discount capture, preflight,
    // readiness retries, and modal close/reopen. Airtable ids and asset links
    // are pre-payment state, not proof that the order session completed.
    const storedKey = normalizeOrderIdempotencyKey(payload?.idempotency_key);
    const refKey = normalizeOrderIdempotencyKey(idempotencyKeyRef.current);
    const { idempotencyKey } = resolveOrderIdempotencyKey(storedKey || refKey);
    idempotencyKeyRef.current = idempotencyKey;
    if (storedKey !== idempotencyKey) {
      try { mergePayload({ idempotency_key: idempotencyKey }); } catch {}
    }
  }, [mergePayload, open, payload?.idempotency_key]);

  useEffect(() => {
    if (!open) return;
    const fallbackName = prefillContact?.name || prefillContact?.fullName || '';
    const fallbackPhone = prefillContact?.phone || '';
    const fallbackEmail = prefillContact?.email || '';
    setName(fallbackName);
    setPhone(fallbackPhone);
    setEmail(fallbackEmail);
  }, [open, prefillContact?.name, prefillContact?.fullName, prefillContact?.phone, prefillContact?.email]);

  const totalItemsCount = typeof getTotalItems === 'function' ? Number(getTotalItems() || 0) : 0;
  const checkoutPricing = priceCart({
    cartItems,
    couponCode: payload?.couponCode,
    discountClaimed,
    withDelivery: payload?.withDelivery,
    totalItemsCount,
    paymentMethod: method,
  });
  const {
    subtotalBeforeDiscount,
    subtotalAfterDiscount,
    discountAmount,
    deliveryCost,
    vatBase,
    vatAmount,
    baseTotal,
    cardDiscount,
    couponDiscount,
    paymentBeforeCoupon,
    paymentTotal,
  } = checkoutPricing;
  const firstItemDiscountRate = cartItems?.find((item) => typeof item?.discountRate === 'number')?.discountRate;
  const inferredDiscountRate = discountAmount > 0 && subtotalBeforeDiscount > 0
    ? discountAmount / subtotalBeforeDiscount
    : (typeof firstItemDiscountRate === 'number' ? firstItemDiscountRate : DEFAULT_DISCOUNT_RATE);
  const discountRatePercent = Math.round((inferredDiscountRate || 0) * 100);
  const totalWithVat = formatCurrency(paymentTotal);

  const handleConfirm = async () => {
    let checkoutPayload = payload;
    if (!methodCards.some(({ id }) => id === method) || checkoutPricing.ok === false) return;
    const contactMissing = !checkoutReadiness.contactComplete;
    const deliveryAddressMissing = !checkoutReadiness.deliveryAddressComplete;
    const checkoutEpisodeId = ensureCheckoutEpisodeId();
    try {
      record('checkout_confirm_clicked', {
        checkoutEpisodeId,
        method,
        cartItems: cartItemCount,
        subtotal: Number(cartSummary?.total ?? 0),
        contactMissing,
        deliveryAddressMissing,
      });
    } catch (_) {}

    if (contactMissing) {
      try {
        record('checkout_validation_error', { reason: 'missing_contact', method });
      } catch (_) {}
      toast({ title: 'אנא מלא/י שם, טלפון ואימייל' , variant: 'destructive'});
      return;
    }

    if (deliveryAddressMissing) {
      try {
        record('checkout_validation_error', {
          reason: 'missing_delivery_address',
          missingFields: checkoutReadiness.missingDeliveryFields,
        });
      } catch (_) {}
      toast({
        title: language === 'he'
          ? 'יש להשלים את כתובת המשלוח'
          : 'Complete the delivery address',
        description: language === 'he'
          ? 'סגרו את חלון התשלום ומלאו רחוב ומספר בית ועיר לפני ההמשך.'
          : 'Close the payment dialog and enter the delivery street, house number and city before continuing.',
        variant: 'destructive',
      });
      return;
    }

    if (!agreedToTerms) {
      try {
        record('checkout_validation_error', { reason: 'terms_not_accepted', method });
      } catch (_) {}
      toast({
        title: language === 'he' ? 'יש לאשר את תנאי השימוש' : 'Please accept the terms',
        variant: 'destructive',
      });
      return;
    }

    trackGa4CheckoutEvent({
      eventName: 'add_payment_info',
      episodeId: checkoutEpisodeId,
      value: Number(cartSummary?.total ?? 0),
      cartItemCount,
      cartUnitCount: checkoutUnitCount,
      paymentType: method,
      items: checkoutAnalyticsItems,
      measurementId: ga4MeasurementId,
    });

    let conversionTime = existingConversionTime || snapshotConversionTime || new Date().toISOString();
    if (!existingConversionTime) {
      try {
        const nextTracking = {
          ...(checkoutPayload?.tracking || {}),
          metadata: {
            ...(checkoutPayload?.tracking?.metadata || {}),
            conversionTime,
          },
        };
        mergePayload({ tracking: nextTracking });
      } catch (_) {}
    }

    // Merge contact + payment method into shared checkoutPayload
    try {
      mergePayload({ contact: { ...(checkoutPayload?.contact || {}), name: name.trim(), phone: phone.trim(), email: email.trim() }, paymentMethod: method });
    } catch (err) {
      // ignore
    }

    try {
      record('checkout_contact_captured', {
        method,
        nameProvided: !!name.trim(),
        phoneProvided: !!phone.trim(),
        emailProvided: !!email.trim(),
      });
    } catch (_) {}

  // Build a sanitized cart (strip File objects) but preserve uploadedDesigns.url (which may contain data URLs)
    const sanitizedCart = (Array.isArray(cartItems) ? cartItems : []).map((i, index) => {
      const { uploadedDesigns, ...rest } = i || {};
      const sanitizedDesigns = {};
      if (uploadedDesigns && typeof uploadedDesigns === 'object') {
        for (const key in uploadedDesigns) {
          const { file, ...restOfDesign } = uploadedDesigns[key] || {};
          const preserved = {};
          if (restOfDesign && restOfDesign.url) preserved.url = restOfDesign.url;
          if (restOfDesign && restOfDesign.originalUrl) preserved.originalUrl = restOfDesign.originalUrl;
          if (restOfDesign && restOfDesign.name) preserved.name = restOfDesign.name;
          sanitizedDesigns[key] = preserved;
        }
      }
      const derived = checkoutPricing.lines[index];
      const effectiveDiscountRate = derived.couponApplied
        ? 0
        : (derived.discountApplied ? (derived.discountRate || DEFAULT_DISCOUNT_RATE) : 0);
      const itemQuantity = computeItemQuantity(rest);
      const unitPriceBeforeDiscount = itemQuantity > 0
        ? Number((derived.subtotalBefore / itemQuantity).toFixed(2))
        : null;
      const printAreaFees = computePrintAreaFees(rest, itemQuantity);
      return {
        ...rest,
        quantity: itemQuantity,
        unitPrice: unitPriceBeforeDiscount,
        totalPrice: derived.subtotalAfter,
        subtotalAfterDiscount: derived.subtotalAfter,
        subtotalBeforeDiscount: derived.subtotalBefore,
        totalBeforeDiscount: derived.subtotalBefore,
        discountAmount: derived.discountAmount,
        discountRate: effectiveDiscountRate,
        discountClaimed: (!checkoutPricing.couponApplied || checkoutPricing.coupon.type === 'order_percent')
          && (derived.discountApplied || rest?.discountClaimed || discountClaimed),
        couponCode: derived.couponApplied ? checkoutPricing.coupon.code : null,
        couponUnitPriceInclVat: derived.couponUnitPriceInclVat,
        priceBreakdown: {
          ...derived.breakdown,
          unitPrice: unitPriceBeforeDiscount,
          discountAmount: derived.discountAmount,
          discountRate: effectiveDiscountRate,
          couponCode: derived.couponApplied ? checkoutPricing.coupon.code : null,
          couponUnitPriceInclVat: derived.couponUnitPriceInclVat,
          subtotalAfterDiscount: derived.subtotalAfter,
          totalBeforeDiscount: derived.subtotalBefore,
          totalAfterDiscount: derived.subtotalAfter,
          merchandiseTotal: derived.subtotalBefore,
          printAreaFees,
        },
        uploadedDesigns: sanitizedDesigns,
      };
    });

    const financeLineItems = sanitizedCart.map((item, index) => {
      const subtotalBefore = Number.isFinite(item.subtotalBeforeDiscount)
        ? Number(item.subtotalBeforeDiscount)
        : (Number.isFinite(item.totalBeforeDiscount) ? Number(item.totalBeforeDiscount) : null);
      const subtotalAfter = Number.isFinite(item.subtotalAfterDiscount)
        ? Number(item.subtotalAfterDiscount)
        : (Number.isFinite(item.totalPrice) ? Number(item.totalPrice) : null);
      const discountValue = Number.isFinite(item.discountAmount) ? Number(item.discountAmount) : 0;
      const quantity = Number.isFinite(item.quantity) ? Number(item.quantity) : computeItemQuantity(item);
      const unitPrice = Number.isFinite(item.priceBreakdown?.unitPrice)
        ? Number(item.priceBreakdown.unitPrice)
        : (Number.isFinite(item.unitPrice) ? Number(item.unitPrice) : (quantity > 0 && Number.isFinite(subtotalBefore)
          ? Number((subtotalBefore / quantity).toFixed(2))
          : null));
      const baseUnitPrice = Number.isFinite(item.priceBreakdown?.unitBase)
        ? Number(item.priceBreakdown.unitBase)
        : null;
      const baseTotal = baseUnitPrice != null && quantity > 0
        ? Number((baseUnitPrice * quantity).toFixed(2))
        : null;
      const printAreaFees = Array.isArray(item.priceBreakdown?.printAreaFees)
        ? item.priceBreakdown.printAreaFees
        : computePrintAreaFees(item, quantity);
      const { productSku, productName, productLabel } = resolveOpsProductRefs(item);
      const label = productLabel;
      const lineText = formatBreakdownLine(label, quantity, unitPrice, subtotalBefore, formatCurrency);
      const areaTexts = printAreaFees
        .map((area) => formatBreakdownLine(`- ${area.label || area.areaKey}`, area.quantity, area.unit_fee, area.total, formatCurrency))
        .filter(Boolean);
      return {
        sku: productSku,
        name: productName,
        colors: normalizeItemColors(item),
        quantity,
        unit_price: unitPrice,
        subtotal_before_discount: subtotalBefore,
        discount: discountValue,
        subtotal_after_discount: subtotalAfter,
        ...(checkoutPricing.coupon.type === 'order_percent'
          ? { discount_claimed: checkoutPricing.lines[index].discountApplied }
          : {}),
        ...(item.couponCode ? { coupon_code: item.couponCode, coupon_unit_price_incl_vat: item.couponUnitPriceInclVat } : {}),
        selected_print_areas: normalizePrintAreas(item),
        base_unit_price: baseUnitPrice,
        base_total: baseTotal,
        print_area_breakdown: printAreaFees,
        line_text: lineText,
        area_texts: areaTexts,
      };
    });

    const financeBreakdownLines = financeLineItems.reduce((lines, entry) => {
      if (entry?.line_text) lines.push(entry.line_text);
      if (Array.isArray(entry?.area_texts)) {
        entry.area_texts.forEach((text) => { if (text) lines.push(text); });
      }
      return lines;
    }, []);
    financeBreakdownLines.push(`Delivery = ${formatCurrency(deliveryCost)}`);
    if (discountAmount > 0) {
      financeBreakdownLines.push(checkoutPricing.couponApplied && checkoutPricing.coupon.type === 'fixed_unit_gross'
        ? `Coupon ${checkoutPricing.coupon.code} = -${formatCurrency(discountAmount)}`
        : checkoutPricing.coupon.type === 'order_percent'
          ? `Claimed merchandise discount = -${formatCurrency(discountAmount)}`
          : `Discount ${discountRatePercent}% = -${formatCurrency(discountAmount)}`);
    }
    financeBreakdownLines.push(`Merchandise and delivery incl. VAT = ${formatCurrency(baseTotal)}`);
    if (cardDiscount > 0) {
      financeBreakdownLines.push(`Card discount 3% = -${formatCurrency(cardDiscount)}`);
    }
    if (couponDiscount > 0) {
      financeBreakdownLines.push(`Total incl. VAT before coupon = ${formatCurrency(paymentBeforeCoupon)}`);
      financeBreakdownLines.push(`Coupon ${checkoutPricing.coupon.code} 99% = -${formatCurrency(couponDiscount)}`);
    }
    financeBreakdownLines.push(`Taxable net amount = ${formatCurrency(vatBase)}`);
    financeBreakdownLines.push(`VAT included (18%) = ${formatCurrency(vatAmount)}`);
    financeBreakdownLines.push(`Total including VAT payable = ${formatCurrency(paymentTotal)}`);

    const totalsBreakdown = {
      merchandise_before_discount: subtotalBeforeDiscount,
      discount: discountAmount,
      merchandise_after_discount: subtotalAfterDiscount,
      delivery: deliveryCost,
      vat: vatAmount,
      total_before_payment_method_adjustments: baseTotal,
      total_after_payment_method_adjustments: paymentTotal,
    };
    totalsBreakdown.taxable_net_after_adjustments = vatBase;
    if (couponDiscount) totalsBreakdown.coupon_discount = couponDiscount;
    if (cardDiscount || couponDiscount) totalsBreakdown.payment_method_adjustments = {
      ...(cardDiscount ? { card_discount: cardDiscount } : {}),
      ...(couponDiscount ? { coupon_discount: couponDiscount } : {}),
    };

    let financialBlock = {
      subtotal: subtotalAfterDiscount,
      subtotal_before_discount: subtotalBeforeDiscount,
      discount: discountAmount,
      delivery: deliveryCost,
      vat: vatAmount,
      total: baseTotal,
      total_after_payment_adjustments: paymentTotal,
      payment_method: method,
      currency: 'ILS',
      discount_claimed: Boolean(discountClaimed),
      with_delivery: Boolean(checkoutPayload?.withDelivery),
      totals_breakdown: totalsBreakdown,
    };
    financialBlock.vat_base = vatBase;
    if (checkoutPricing.couponApplied) financialBlock.coupon_code = checkoutPricing.coupon.code;
    else financialBlock.discount_rate_percent = discountRatePercent;
    if (couponDiscount) {
      financialBlock.coupon_discount = couponDiscount;
      financialBlock.coupon_discount_rate_percent = 99;
    }
    if (cardDiscount || couponDiscount) financialBlock.payment_adjustments = {
      ...(cardDiscount ? { card_discount: cardDiscount } : {}),
      ...(couponDiscount ? { coupon_discount: couponDiscount } : {}),
    };
    if (cardDiscount) financialBlock.card_discount = cardDiscount;
    if (financeLineItems.length) {
      financialBlock.line_items = financeLineItems;
    }
    if (financeBreakdownLines.length) {
      financialBlock.breakdown_lines = financeBreakdownLines;
      financialBlock.breakdown_text = financeBreakdownLines.join('\n');
    }
    if (checkoutPayload?.financial?.dropbox_shared_link) {
      financialBlock.dropbox_shared_link = checkoutPayload.financial.dropbox_shared_link;
    }
    if (checkoutPayload?.financial?.dropbox_worksheet_link) {
      financialBlock.dropbox_worksheet_link = checkoutPayload.financial.dropbox_worksheet_link;
    }
    if (checkoutPayload?.financial?.dropbox_worksheet_links) {
      financialBlock.dropbox_worksheet_links = checkoutPayload.financial.dropbox_worksheet_links;
    }
    const baseFinancialForDocs = {
      subtotal: financialBlock.subtotal,
      delivery: financialBlock.delivery,
      vat_base: vatBase,
      vat: financialBlock.vat,
      total: financialBlock.total,
      total_after_payment_adjustments: financialBlock.total_after_payment_adjustments,
      payment_method: financialBlock.payment_method,
    };

    let toSend = {
      ...checkoutPayload,
      cart: sanitizedCart,
      contact: { ...(checkoutPayload?.contact || {}), name: name.trim(), phone: phone.trim(), email: email.trim() },
      paymentMethod: method,
      // Provide totals to satisfy backend validation; keep currency simple for now
      totals: {
        subtotal: subtotalBeforeDiscount,
        discount: discountAmount,
        total: subtotalAfterDiscount,
        currency: 'ILS',
      },
    };

    let actionLogForAirtable = '';
    try {
      actionLogForAirtable = getFormattedLog();
    } catch (_) {}

    try {
      record('checkout_payload_ready', {
        method,
        cartItems: sanitizedCart.length,
        subtotal: subtotalAfterDiscount,
        delivery: deliveryCost,
      });
    } catch (_) {}

    const requiresDurablePaymentContext = method === 'card' || method === 'bit';
    // Both available methods must pass the canonical gross-price order preflight.
    const requiresOrderPersistence = true;
    if (confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    setProcessingMessage('מכין את התשלום…');
    setProcessing(true);
    try {

    // Preserve every earlier attempt. If it cannot be continued, the owner has
    // authorized a separate fresh order despite the unresolved-charge risk.
    if (requiresOrderPersistence) {
      try {
        const preparation = await preparePaymentCheckout({
          orderKey: idempotencyKeyRef.current,
          amountAgorot: Math.round(paymentTotal * 100), paymentMethod: method,
          makeCheckoutIntent: (signal) => buildCheckoutRetryIntent({
            signal,
            cartItems: sanitizedCart, amount: paymentTotal, paymentMethod: method,
            withDelivery: Boolean(checkoutPayload?.withDelivery), customer: {
              name, phone, email,
              address_street: checkoutPayload?.contact?.street || checkoutPayload?.contact?.address_street || '',
              address_city: checkoutPayload?.contact?.city || checkoutPayload?.contact?.address_city || '',
            },
          }),
        });
        if (preparation.kind === 'continue') {
          window.location.assign(preparation.url);
          return;
        }
        if (preparation.kind === 'fresh') {
          const fresh = persistFreshCheckoutSession({
            ...toSend,
            contact: { ...(checkoutPayload?.contact || {}), ...toSend.contact },
            customer: { name, phone, email,
              address_street: checkoutPayload?.contact?.street || checkoutPayload?.contact?.address_street || '',
              address_city: checkoutPayload?.contact?.city || checkoutPayload?.contact?.address_city || '' },
            financial: financialBlock,
          }, { previousOrderKey: idempotencyKeyRef.current, reason: preparation.reason });
          // Persistence succeeded before any new order/provider request. Every
          // downstream stage uses this task-local snapshot and the same new key.
          checkoutPayload = fresh;
          toSend = fresh;
          financialBlock = fresh.financial;
          conversionTime = fresh.tracking.metadata.conversionTime;
          idempotencyKeyRef.current = fresh.idempotency_key;
          airtableRecordIdRef.current = '';
          checkoutEpisodeIdRef.current = '';
          setPayload(fresh);
          record('checkout_fresh_payment_session', {
            previousOrderKey: fresh.payment_recovery.previous_order_idempotency_key,
            orderKey: fresh.idempotency_key, reason: fresh.payment_recovery.reason,
            riskAcceptance: fresh.payment_recovery.risk_acceptance,
          });
          actionLogForAirtable = getFormattedLog();
        }
      } catch (error) {
        toast({ title: 'התשלום לא נפתח', variant: 'destructive',
          description: ['order_already_paid', 'payment_already_collected'].includes(error.code)
            ? 'התשלום עבור ההזמנה כבר אומת. אין לשלם שוב. פנו אלינו אם נדרש עדכון להזמנה.'
            : 'לא הצלחנו להכין את התשלום. נסו שוב בעוד רגע או פנו אלינו.',
        });
        setProcessing(false);
        return;
      }
    }

    // Post the separate draft only after the validated Airtable order is saved.
    const postOrder = () => {
      try {
        const json = JSON.stringify(toSend);
        let sent = false;
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          const blob = new Blob([json], { type: 'application/json' });
          sent = navigator.sendBeacon('/api/order', blob);
        }
        if (!sent) {
          // do not await; avoid blocking UI
          checkoutFetch('/api/order', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: json,
            keepalive: true,
          }).catch(() => {});
        }
        try {
          record('checkout_order_posted', {
            method,
            transport: sent ? 'beacon' : 'fetch',
            payloadBytes: json.length,
          });
        } catch (_) {}
      } catch (_) {}
    };

    let submittedOrderId = '';
    // Enrich Airtable record with Customer + Financial + Cart and attach file paths.
    try {
      // First: upsert order in Airtable to get orderId and (optionally) a Dropbox link
      let orderIdFromAirtable = checkoutPayload?.airtable_order_id || '';
      let dropboxSharedLink = checkoutPayload?.financial?.dropbox_shared_link || '';
      let airtableRecordId = airtableRecordIdRef.current || '';

      try {
        const googleAds = googleAdsSnapshot;
        const trackingMetadata = metadataSnapshot;
        const gclidValue = googleAds.gclid || '';
        const campaignValue = googleAds.campaign || '';
        const searchValue = googleAds.keyword || '';
        const deviceValue = trackingMetadata.device || '';
        const cartItemsPayload = buildCartItemsForAirtable(sanitizedCart);
        setProcessingMessage('שומר את פרטי ההזמנה…');
        const prelim = await checkoutFetch('/api/airtable/order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idempotency_key: idempotencyKeyRef.current,
            order_stage: 'confirmed_checkout',
            checkout_preflight: true,
            customerActionLog: actionLogForAirtable,
            gclid: gclidValue,
            campaign: campaignValue,
            search: searchValue,
            device: deviceValue,
            conversion_time: conversionTime,
            customer: {
              name,
              phone,
              email,
              address_street: checkoutPayload?.contact?.street || checkoutPayload?.contact?.address_street || '',
              address_city: checkoutPayload?.contact?.city || checkoutPayload?.contact?.address_city || '',
            },
            financial: financialBlock,
            cart: {
              items: (cartItems || []).map((i) => ({
                ...resolveOpsProductRefs(i),
                sizeMatrices: (i.sizeMatrices && typeof i.sizeMatrices === 'object') ? i.sizeMatrices : (i.color ? { [i.color]: (i.sizeMatrix || {}) } : {}),
                selectedPrintAreas: (i.selectedPrintAreas || []).map((sel) => typeof sel === 'string' ? { areaKey: sel, method: 'print' } : sel).filter(Boolean),
                discountClaimed: i.discountClaimed === true,
              })),
            },
            uploads: [],
            cartUploads: [],
          }),
        });
        const prelimJson = await prelim.json().catch(() => null);
        if (prelimJson?.checkoutConfirmedAt) {
          financialBlock.checkout_confirmed_at = prelimJson.checkoutConfirmedAt;
        }
        try {
          record('checkout_airtable_preflight', {
            ok: prelimJson?.ok !== false,
            recordId: prelimJson?.record?.id || null,
            orderId: prelimJson?.orderId || null,
          });
        } catch (_) {}
        if (
          requiresOrderPersistence &&
          (
            !prelim.ok ||
            prelimJson?.ok !== true ||
            !prelimJson?.record?.id ||
            !prelimJson?.orderId
          )
        ) {
          throw new Error(
            method === 'card'
              ? 'card_order_preflight_failed'
              : method === 'bit'
                ? 'bit_order_preflight_failed'
                : 'order_preflight_failed',
          );
        }
        if (prelimJson && prelimJson.orderId) {
          orderIdFromAirtable = prelimJson.orderId;
        }
        if (prelimJson && prelimJson.record && prelimJson.record.id) {
          airtableRecordId = prelimJson.record.id;
          airtableRecordIdRef.current = prelimJson.record.id;
          try { mergePayload({ airtable_record_id: prelimJson.record.id }); } catch {}
        }
        if (prelimJson && prelimJson.dropbox && prelimJson.dropbox.shared_link) {
          dropboxSharedLink = prelimJson.dropbox.shared_link;
        }
        if (orderIdFromAirtable) {
          try { mergePayload({ airtable_order_id: orderIdFromAirtable }); } catch {}
        }
        if (dropboxSharedLink) {
          try { mergePayload({ financial: { ...(checkoutPayload?.financial || {}), dropbox_shared_link: dropboxSharedLink } }); } catch {}
          financialBlock.dropbox_shared_link = dropboxSharedLink;
        }
        if (!airtableRecordId && checkoutPayload?.airtable_record_id) {
          airtableRecordId = checkoutPayload.airtable_record_id;
        }
      } catch (err) {
        try {
          record('checkout_airtable_preflight_exception', { error: err?.message || String(err) });
        } catch (_) {}
        if (requiresOrderPersistence) throw err;
      }

      const ensureDataUrl = async (maybeUrl) => {
        try {
          if (!maybeUrl || typeof maybeUrl !== 'string') return '';
          if (maybeUrl.startsWith('data:')) return maybeUrl;

          // Convert blob:/http(s): URLs to a data URL so the serverless API can upload to Dropbox.
          const resp = await checkoutFetch(maybeUrl);
          if (!resp.ok) return '';
          const blob = await resp.blob();
          if (!blob) return '';

          const reader = new FileReader();
          const result = await withCheckoutDeadline((signal) => new Promise((resolve, reject) => {
            const onAbort = () => { reader.abort(); reject(signal.reason); };
            signal.addEventListener('abort', onAbort, { once: true });
            reader.onloadend = () => signal.removeEventListener('abort', onAbort);
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve('');
            try {
              reader.readAsDataURL(blob);
            } catch {
              resolve('');
            }
          }), { timeoutMs: 15000 });

          return (typeof result === 'string' && result.startsWith('data:')) ? result : '';
        } catch {
          return '';
        }
      };

      // Recreate the same uploads we generated in Cart for naming consistency
      const uploads = (async () => {
        try {
          const list = [];
          // helper to push an upload record
          const pushUpload = (u) => { if (u) list.push(u); };

	          console.log('[CheckoutModal] building uploads from cart', { cartItemCount: (cartItems || []).length });
	          for (const [itemIndex, item] of (cartItems || []).entries()) {
	            const { productSku, productName } = resolveOpsProductRefs(item);
	            const product = productSku || 'product';
	            const productRecord = products.find((entry) => entry.sku === product) || null;
	            const matrices = item.sizeMatrices || {};
            const colors = item.selectedColors && Array.isArray(item.selectedColors) && item.selectedColors.length
              ? item.selectedColors
              : (item.color ? [item.color] : []);
            const activeColors = [];
            let totalQtyForItem = 0;
            for (const c of colors) {
              const mat = (matrices && matrices[c]) || (c === item.color ? (item.sizeMatrix || {}) : {});
              const qty = Object.values(mat || {}).reduce((s, q) => s + (q || 0), 0);
              if (qty > 0) { activeColors.push(c); totalQtyForItem += qty; }
            }
            if (activeColors.length === 0) continue;
            const areaMethod = {};
            (item.selectedPrintAreas || []).forEach((sel) => {
              if (!sel) return;
              if (typeof sel === 'string') areaMethod[sel] = 'print';
              else if (sel.areaKey) areaMethod[sel.areaKey] = sel.method || 'print';
            });
            const designs = item.uploadedDesigns || {};
            console.log('[CheckoutModal] item designs', { product, designKeys: Object.keys(designs), hasDesigns: Object.keys(designs).length > 0 });
		            for (const areaKey of Object.keys(designs)) {
		              if (!areaMethod[areaKey]) continue;
		              const d = designs[areaKey];
		              if (!d || !d.url) continue;
	              const method = areaMethod[areaKey] || 'print';
              const fileName = d.name || `${areaKey}.png`;
              // original design upload: prefer originalUrl (for PDFs) if present
              const originalOrPreview = (d.originalUrl && typeof d.originalUrl === 'string') ? d.originalUrl : d.url;
              console.log('[CheckoutModal] resolving upload URL', { areaKey, fileName, hasOriginalUrl: !!d.originalUrl, originalOrPreview: originalOrPreview?.slice(0, 50) });
              const resolvedOriginal = await ensureDataUrl(originalOrPreview);
              console.log('[CheckoutModal] resolvedOriginal', { areaKey, resolved: !!resolvedOriginal, length: resolvedOriginal?.length || 0 });
              const resolvedFallback = (!resolvedOriginal && d.url) ? await ensureDataUrl(d.url) : '';
              const resolved = resolvedOriginal || resolvedFallback;
              console.log('[CheckoutModal] final resolved', { areaKey, hasResolved: !!resolved, willPush: !!resolved });
	              if (resolved) {
	                pushUpload({ areaKey, method, product, productName, colors: activeColors, qty: totalQtyForItem, dataUrl: resolved, fileName });
	              } else {
	                console.warn('[CheckoutModal] skipping upload - no valid data URL', { areaKey, fileName, hadOriginalUrl: !!d.originalUrl, hadUrl: !!d.url });
	              }

	              try {
	                if (!productRecord) continue;
	                const side = areaKey.startsWith('back') ? 'back' : 'front';
	                const baseImage = getProductSideImage(productRecord, side, activeColors, { usePlainFrontWhenNoColor: true })
	                  || `/schematics/${side}.png`;
	                const mockupDataUrl = await composeMockupImage({
	                  areaKey,
	                  baseImage,
	                  designUrl: d.url,
	                  template: productRecord.sku,
	                });
	                if (mockupDataUrl) {
	                  pushUpload({
	                    areaKey,
	                    method: 'mockup',
	                    product,
	                    productName,
	                    colors: activeColors,
	                    qty: totalQtyForItem,
	                    dataUrl: mockupDataUrl,
	                    fileName: `${areaKey}-mockup.png`,
	                  });
	                }
	              } catch (mockupErr) {
	                console.warn('[CheckoutModal] mockup generation skipped', {
	                  product,
	                  areaKey,
	                  error: mockupErr?.message || String(mockupErr),
	                });
	              }
	            }
            // Each cart line requires its own complete worksheet, even when
            // multiple independent lines share a product SKU.
            const pdfDataUrl = await generateRequiredWorksheetPdf({
              composePage: composeWorksheetImage,
              toPdf: pngsToPdf,
              itemIndex,
              input: {
                item,
                language: 'en',
                dropboxLink: dropboxSharedLink || (checkoutPayload?.financial?.dropbox_shared_link || ''),
                idempotencyKey: idempotencyKeyRef.current,
                orderNumber: (orderIdFromAirtable || checkoutPayload?.airtable_order_id || ''),
                customer: { name, phone, email, address_street: checkoutPayload?.contact?.street || checkoutPayload?.contact?.address_street || '', address_city: checkoutPayload?.contact?.city || checkoutPayload?.contact?.address_city || '' },
                financial: baseFinancialForDocs,
                delivery: { method: checkoutPayload?.withDelivery ? 'Delivery' : 'Pickup', notes: checkoutPayload?.deliveryNotes || '' },
              },
            });
            pushUpload({ areaKey: 'worksheet', method: 'worksheet', itemIndex, product, productName, colors: activeColors, qty: totalQtyForItem, dataUrl: pdfDataUrl, fileName: `worksheet-item-${itemIndex + 1}.pdf` });
          }
          console.log('[CheckoutModal] built uploads', { totalCount: list.length });
          return list;
        } catch (e) { throw e; }
      })();

      setProcessingMessage('מכין את קובצי ההזמנה…');
      const builtUploads = await uploads;
      const worksheetUploads = builtUploads.filter((entry) => entry.method === 'worksheet');
      if (worksheetUploads.length !== sanitizedCart.length) {
        const missingIndex = sanitizedCart.findIndex((_, index) => !worksheetUploads.some((entry) => entry.itemIndex === index));
        throw new WorksheetArtifactError('worksheet_render_empty', 'render', missingIndex);
      }
      console.log('[CheckoutModal] built uploads DETAILED', JSON.stringify(builtUploads.map(u => ({ 
        areaKey: u.areaKey, 
        method: u.method, 
        product: u.product, 
        fileName: u.fileName,
        hasDataUrl: !!u.dataUrl,
        dataUrlLength: u.dataUrl?.length || 0
      })), null, 2));

      // Upload ALL files (originals, worksheets, mockups) to Cloudinary
      let cloudinaryBlock = null;
      try {
        const oid = (orderIdFromAirtable || checkoutPayload?.airtable_order_id || '').trim();
        console.log('[CheckoutModal] uploading to cloudinary', { orderId: oid, uploadsCount: builtUploads.length });
        setProcessingMessage('מעלה את קובצי ההזמנה…');
        const cloudRes = await uploadCheckoutFilesToCloudinary({ orderId: oid, uploads: builtUploads });
        assertWorksheetCloudinaryUploads(worksheetUploads, cloudRes);
        console.log('[CheckoutModal] cloudinary upload result', { ok: cloudRes?.ok, filesCount: cloudRes?.files?.length || 0, skipped: cloudRes?.skipped, reason: cloudRes?.reason });
        cloudinaryBlock = {
          folder_base: `printeam/user_logos/${oid}`,
          files: cloudRes?.files || [],
          ok: cloudRes?.ok === true,
          skipped: !!cloudRes?.skipped,
          reason: cloudRes?.reason || undefined,
        };
        console.log('[CheckoutModal] cloudinaryBlock built', { hasBlock: !!cloudinaryBlock, filesCount: cloudinaryBlock?.files?.length || 0 });
      } catch (_) {
        if (_ instanceof WorksheetArtifactError) throw _;
        throw new WorksheetArtifactError('worksheet_cloudinary_failed', 'cloudinary');
      }
      const cartItemsPayload = buildCartItemsForAirtable(sanitizedCart);
      const enriched = {
        idempotency_key: idempotencyKeyRef.current,
        require_worksheets: true,
        gclid: googleAdsSnapshot.gclid || '',
        campaign: googleAdsSnapshot.campaign || '',
        search: googleAdsSnapshot.keyword || '',
        device: metadataSnapshot.device || '',
        conversion_time: conversionTime,
        customer: {
          name,
          phone,
          email,
          // Map our DeliveryOptions contact shape (street/city) into canonical fields
          address_street: checkoutPayload?.contact?.street || checkoutPayload?.contact?.address_street || '',
          address_city: checkoutPayload?.contact?.city || checkoutPayload?.contact?.address_city || '',
        },
        financial: financialBlock,
        cart: {
          items: cartItemsPayload,
        },
        // Send Cloudinary URLs instead of data URLs (payload size limit workaround)
        // Backend will download from Cloudinary and upload to Dropbox
	        uploads: (cloudinaryBlock?.files || []).filter((f) => f?.secure_url).map(f => ({
	          areaKey: f.areaKey,
	          method: f.method || 'print',
	          itemIndex: f.itemIndex,
	          product: f.productSku,
	          productName: f.productName || '',
	          colors: f.colors,
	          qty: f.qty,
	          cloudinaryUrl: f.secure_url,
	          fileName: f.originalName,
	          format: f.format,
	        })),
        // Cloudinary originals metadata
        ...(cloudinaryBlock ? { cloudinary: cloudinaryBlock } : {}),
        // Lightweight metadata for Airtable payload (avoid duplicating dataUrl)
        cartUploads: (builtUploads || []).map((u) => ({
          areaKey: u.areaKey,
          method: u.method,
          itemIndex: u.itemIndex,
          product: u.product,
          productName: u.productName || '',
          colors: u.colors,
          qty: u.qty,
          fileName: u.fileName,
        })),
        customerActionLog: actionLogForAirtable,
      };

      try {
        setProcessingMessage('שומר את קובצי ההזמנה…');
        const resp = await checkoutFetch('/api/airtable/order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(enriched),
        });
        try {
          const j = await resp.json().catch(() => null);
          if (j?.error === 'worksheet_persistence_failed') {
            throw new WorksheetArtifactError('worksheet_persistence_failed', 'dropbox');
          }
          if (
            requiresOrderPersistence &&
            (
              !resp.ok ||
              j?.ok !== true ||
              !j?.record?.id ||
              !j?.orderId
            )
          ) {
            throw new Error(
              method === 'card'
                ? 'card_order_enrichment_failed'
                : method === 'bit'
                  ? 'bit_order_enrichment_failed'
                  : 'order_enrichment_failed',
            );
          }
          assertWorksheetPersistence(worksheetUploads, j);
          submittedOrderId = j.orderId;
          if (j && j.record && j.record.id) {
            airtableRecordIdRef.current = j.record.id;
            try { mergePayload({ airtable_record_id: j.record.id }); } catch (e) {}
          }
          const link = j && j.dropbox && j.dropbox.shared_link;
          if (link) {
            try { mergePayload({ financial: { ...(checkoutPayload?.financial || {}), dropbox_shared_link: link } }); } catch (e) {}
          }
          try {
            record('checkout_airtable_patch', {
              ok: j?.ok !== false,
              recordId: j?.record?.id || airtableRecordIdRef.current || null,
              orderId: j?.orderId || null,
            });
          } catch (_) {}
        } catch (jsonErr) {
          try {
            record('checkout_airtable_patch_parse_error', { error: jsonErr?.message || String(jsonErr) });
          } catch (_) {}
          if (requiresOrderPersistence) throw jsonErr;
        }
      } catch (patchErr) {
        try {
          record('checkout_airtable_patch_exception', { error: patchErr?.message || String(patchErr) });
        } catch (_) {}
        if (requiresOrderPersistence) throw patchErr;
      }
    } catch (blockErr) {
      try {
        record('checkout_airtable_block_exception', { error: blockErr?.message || String(blockErr) });
      } catch (_) {}
      if (requiresOrderPersistence) {
        if (blockErr instanceof WorksheetArtifactError) {
          try {
            record('checkout_worksheet_failed', {
              code: blockErr.code,
              stage: blockErr.stage,
              itemIndex: blockErr.itemIndex,
              method,
            });
            // Persist the failure through the existing log route, without
            // replaying the order, regenerating assets, or launching payment.
            const logResponse = await checkoutFetch('/api/airtable/order', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                idempotency_key: idempotencyKeyRef.current,
                record_id: airtableRecordIdRef.current || checkoutPayload?.airtable_record_id || '',
                action_log_only: true,
                worksheet_error: {
                  code: blockErr.code,
                  stage: blockErr.stage,
                  itemIndex: blockErr.itemIndex,
                },
              }),
            });
            const logResult = await logResponse.json().catch(() => null);
            if (!logResponse.ok || logResult?.ok !== true || !logResult?.record?.id) {
              record('checkout_worksheet_log_flush_failed', { reason: 'log_route_failed' });
            }
          } catch (_) {
            try { record('checkout_worksheet_log_flush_failed', { reason: 'log_route_unavailable' }); } catch (_) {}
          }
        }
        const isCard = method === 'card';
        toast({
          title: isCard ? 'התשלום בכרטיס לא נפתח' : method === 'bit' ? 'התשלום ב-Bit לא נפתח' : 'ההזמנה לא הושלמה',
          description: blockErr instanceof WorksheetArtifactError
            ? 'דף העבודה של ההזמנה לא נוצר או לא נשמר. עצרנו לפני התשלום. פרטי ההזמנה עשויים כבר להופיע אצלנו; נסו שוב או צרו איתנו קשר.'
            : 'פרטי ההזמנה לא נשמרו במלואם ולכן עצרנו לפני התשלום. נסו שוב או צרו איתנו קשר.',
          variant: 'destructive',
        });
        setProcessing(false);
        return;
      }
    }
    postOrder();

    try {
      const recordIdForFlush = airtableRecordIdRef.current || checkoutPayload?.airtable_record_id || '';
      if (actionLogForAirtable) {
        clearLogs();
        try {
          record('checkout_flush_via_server', { recordId: recordIdForFlush || null });
        } catch (_) {}
      } else if (recordIdForFlush) {
        try {
          record('checkout_prepare_flush', { recordId: recordIdForFlush });
        } catch (_) {}
        const flushResult = await withCheckoutDeadline(
          (signal) => flushLogsToAirtable(recordIdForFlush, { signal }), { timeoutMs: 5000 });
        if (!flushResult.ok) {
          try {
            record('checkout_flush_failed', {
              recordId: recordIdForFlush,
              error: flushResult.error?.message || String(flushResult.error || 'unknown'),
            });
          } catch (_) {}
        }
      } else {
        try {
          record('checkout_flush_skipped', { reason: 'missing_record_id' });
        } catch (_) {}
      }
    } catch (err) {
      try {
        record('checkout_flush_exception', { error: err?.message || String(err) });
      } catch (_) {}
    }

    let capturedPaymentContext = null;
    let paymentAttemptKey = '';
    let collectionProvider = 'icount';
    if (requiresDurablePaymentContext) {
      try {
        const analyticsIdentifiers = await readGoogleAnalyticsIdentifiers({
          measurementId: (import.meta?.env?.VITE_GA4_MEASUREMENT_ID || 'G-43KTYPJPNM').trim(),
        });
        const analyticsItems = checkoutAnalyticsItems;
        try {
          record('checkout_conversion_context_captured', {
            hasGoogleClickId: Boolean(
              googleAdsSnapshot.gclid ||
              googleAdsSnapshot.gbraid ||
              googleAdsSnapshot.wbraid
            ),
            hasGaClientId: Boolean(analyticsIdentifiers.clientId),
            hasGaSessionId: Boolean(analyticsIdentifiers.sessionId),
            ga4ItemRows: analyticsItems.length,
          });
        } catch (_) {}
        setProcessingMessage('מכין את התשלום…');
        const readinessResponse = await checkoutFetch('/api/payments/readiness', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idempotency_key: idempotencyKeyRef.current,
            payment_attempt_contract_version: 2,
            amount: paymentTotal,
            currency: 'ILS',
            payment_method: method,
            openaiAds: getOpenAIAdsContext(),
            attribution: {
              gclid: googleAdsSnapshot.gclid || '',
              gbraid: googleAdsSnapshot.gbraid || '',
              wbraid: googleAdsSnapshot.wbraid || '',
              google_click_id_conflict: googleAdsSnapshot.googleClickIdConflict === true,
              google_click_captured_at: googleAdsSnapshot.lastCapturedAt || '',
              google_click_capture_version: googleAdsSnapshot.clickCaptureVersion,
              google_click_capture_source: googleAdsSnapshot.clickCaptureSource || '',
              google_click_candidates: googleAdsSnapshot.google_click_candidates || [],
              google_click_candidate_overflow: googleAdsSnapshot.googleClickCandidateOverflow === true,
              ga_client_id: analyticsIdentifiers.clientId || '',
              ga_session_id: analyticsIdentifiers.sessionId || '',
              ga4_items: analyticsItems,
            },
          }),
        });
        const readiness = await readinessResponse.json().catch(() => null);
        if (
          !readinessResponse.ok
          || readiness?.ready !== true
          || typeof readiness?.payment_attempt_key !== 'string'
          || !readiness.payment_attempt_key.trim()
        ) {
          try {
            record(
              method === 'card'
                ? 'checkout_card_readiness_blocked'
                : 'checkout_bit_readiness_blocked',
              {
                status: readinessResponse.status,
                state: readiness?.state || 'invalid_response',
              },
            );
          } catch (_) {}
          const isCard = method === 'card';
          const priorAttemptNeedsReview = readinessResponse.status === 409
            && ['prior_attempt_unverified', 'prior_attempt_issued'].includes(readiness?.state);
          toast({
            title: isCard ? 'התשלום בכרטיס לא נפתח' : 'התשלום ב-Bit לא נפתח',
            description: readiness?.state === 'payment_already_collected'
              ? 'התשלום עבור ההזמנה כבר אומת. אנו משלימים את אישור ההזמנה. אין לשלם שוב עבור ההזמנה הזאת.'
              : priorAttemptNeedsReview
              ? 'קיים ניסיון תשלום קודם עבור ההזמנה, ואיננו יכולים לוודא כרגע אם בוצע חיוב. פנו אלינו לבדיקת התשלום לפני ניסיון נוסף.'
              : 'לא הצלחנו לאמת שההזמנה מוכנה לתשלום. פנו אלינו אם כבר ניסיתם לשלם לפני ניסיון נוסף.',
            variant: 'destructive',
          });
          setProcessing(false);
          return;
        }
        paymentAttemptKey = readiness.payment_attempt_key.trim();
        collectionProvider = collectionProviderFromReadiness(readiness, method);
        capturedPaymentContext = {
          paymentMethod: method,
          idempotencyKey: idempotencyKeyRef.current,
          paymentAttemptKey,
        };
        try {
          record(
            method === 'card'
              ? 'checkout_card_readiness_confirmed'
              : 'checkout_bit_readiness_confirmed',
            { state: readiness.state },
          );
        } catch (_) {}
      } catch (e) {
        try {
          record(
            method === 'card'
              ? 'checkout_card_readiness_blocked'
              : 'checkout_bit_readiness_blocked',
            { error: e?.message || String(e) },
          );
        } catch (_) {}
        const isCard = method === 'card';
        toast({
          title: isCard ? 'התשלום בכרטיס לא נפתח' : 'התשלום ב-Bit לא נפתח',
          description: 'לא הצלחנו לאמת שההזמנה מוכנה לתשלום. פנו אלינו אם כבר ניסיתם לשלם לפני ניסיון נוסף.',
          variant: 'destructive',
        });
        setProcessing(false);
        return;
      }
    }

    let cardCheckoutUrl = null;
    if (method === 'card' && collectionProvider === 'icount') {
      // The customer card path is pinned to the dedicated Printeam page in the
      // Complog iCount company. Do not accept a client-side account/page override.
      try {
        const firstItem = (cartItems && cartItems[0]) || null;
        const itemName = firstItem ? resolveOpsProductRefs(firstItem).productLabel : 'Product';
        const totalQty = typeof getTotalItems === 'function'
          ? Number(getTotalItems() || 0)
          : (Array.isArray(cartItems) ? cartItems.length : 0);
        const idem = encodeURIComponent(idempotencyKeyRef.current);
        cardCheckoutUrl = buildIcountComplogCheckoutUrl({
          amount: paymentTotal,
          idempotencyKey: paymentAttemptKey,
          description: `${itemName} ${totalQty} כולל מיתוג`,
          name,
          email,
          phone,
          successUrl: `${window.location.origin}/thank-you-cc?idem=${idem}`,
          failureUrl: `${window.location.origin}/cart?status=failed`,
          cancelUrl: `${window.location.origin}/cart`,
        });

      } catch (e) {
        try {
          record('checkout_card_launch_blocked', { error: e?.message || String(e) });
        } catch (_) {}
        toast({
          title: 'התשלום בכרטיס לא נפתח',
          description: 'אירעה תקלה לפני המעבר לתשלום. פנו אלינו אם כבר ניסיתם לשלם לפני ניסיון נוסף.',
          variant: 'destructive',
        });
        setProcessing(false);
        return;
      }
    }

    if (requiresDurablePaymentContext) {
      try {
        const launchResponse = await checkoutFetch(collectionProvider === 'takbull' ? '/api/takbull/launch' : '/api/icount/launch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idempotency_key: idempotencyKeyRef.current,
            payment_attempt_key: paymentAttemptKey,
          }),
        });
        const launch = await launchResponse.json().catch(() => null);
        if (
          !launchResponse.ok
          || launch?.launched !== true
          || launch?.payment_attempt_key !== paymentAttemptKey
        ) {
          try {
            record('checkout_payment_launch_blocked', {
              status: launchResponse.status,
              state: launch?.state || 'invalid_response',
            });
          } catch (_) {}
          const priorAttemptNeedsReview = launchResponse.status === 409
            && ['prior_attempt_unverified', 'prior_attempt_issued', 'attempt_superseded'].includes(launch?.state);
          toast({
            title: method === 'card' ? 'התשלום בכרטיס לא נפתח' : 'התשלום ב-Bit לא נפתח',
            description: launch?.state === 'payment_already_collected'
              ? 'התשלום עבור ההזמנה כבר אומת. אנו משלימים את אישור ההזמנה. אין לשלם שוב עבור ההזמנה הזאת.'
              : priorAttemptNeedsReview
              ? 'קיים ניסיון תשלום קודם או שניסיון התשלום השתנה, ואיננו יכולים לוודא כרגע אם בוצע חיוב. פנו אלינו לבדיקת התשלום לפני ניסיון נוסף.'
              : 'לא הצלחנו לאשר את פתיחת התשלום. פנו אלינו לבדיקת מצב התשלום לפני ניסיון נוסף.',
            variant: 'destructive',
          });
          setProcessing(false);
          return;
        }
        if (collectionProvider === 'takbull') {
          cardCheckoutUrl = validatedTakbullCheckoutUrl(launch.payment_url);
          if (launch.collection_provider !== 'takbull' || !cardCheckoutUrl) throw new Error('invalid_takbull_launch');
        }
        try {
          record('checkout_payment_launch_confirmed', { paymentMethod: method });
        } catch (_) {}
      } catch (e) {
        try {
          record('checkout_payment_launch_blocked', { error: e?.message || String(e) });
        } catch (_) {}
        toast({
          title: method === 'card' ? 'התשלום בכרטיס לא נפתח' : 'התשלום ב-Bit לא נפתח',
          description: 'לא הצלחנו לאשר את פתיחת התשלום. פנו אלינו לבדיקת מצב התשלום לפני ניסיון נוסף.',
          variant: 'destructive',
        });
        setProcessing(false);
        return;
      }

    }

    const purchaseDetails = () => {
      const purchaseItems = sanitizedCart.flatMap((item) => {
        const { productSku, productName } = resolveOpsProductRefs(item);
        const rows = buildGa4PurchaseItems([{
          itemId: productSku,
          itemName: productName,
          sizeMatrices: item.sizeMatrices || (item.color ? { [item.color]: item.sizeMatrix || {} } : {}),
          colors: normalizeItemColors(item),
          quantity: item.quantity,
        }]);
        return rows.map((row) => ({
          ...row,
          price: item.subtotalAfterDiscount / item.quantity,
          discount: item.discountAmount / item.quantity,
          ...(item.couponCode ? { coupon: item.couponCode } : {}),
        }));
      });
      return {
        contact: { ...(checkoutPayload?.contact || {}), name: name.trim(), email: email.trim(), phone: phone.trim() },
        items: purchaseItems,
        financial: financialBlock,
      };
    };

    if (method === 'card') {
      try {
        try {
          savePurchaseContext({ idempotencyKey: idempotencyKeyRef.current, ...purchaseDetails() });
        } catch (_) {
          // Optional analytics enrichment must never prevent the payment redirect.
        }

        setProcessing(false);
        onClose();
        window.location.assign(cardCheckoutUrl.toString());
      } catch (e) {
        try {
          record('checkout_card_launch_blocked', { error: e?.message || String(e) });
        } catch (_) {}
        toast({
          title: 'התשלום בכרטיס לא נפתח',
          description: 'לא הצלחנו להעביר אתכם לתשלום. פנו אלינו לבדיקת מצב התשלום לפני ניסיון נוסף.',
          variant: 'destructive',
        });
        setProcessing(false);
      }
      return;
    }

    if (method === 'bit') {
      if (
        capturedPaymentContext?.paymentMethod !== 'bit'
        || capturedPaymentContext?.idempotencyKey !== idempotencyKeyRef.current
        || capturedPaymentContext?.paymentAttemptKey !== paymentAttemptKey
      ) {
        toast({
          title: 'התשלום ב-Bit לא נפתח',
          description: 'לא הצלחנו לאמת את הקשר לתשלום. פרטי ההעברה לא הוצגו.',
          variant: 'destructive',
        });
        setProcessing(false);
        return;
      }
      try {
        queueSubmittedBitPurchase({
          submission: {
            submitted: true,
            payment_method: 'bit',
            transaction_id: submittedOrderId,
            idempotency_key: idempotencyKeyRef.current,
            currency: 'ILS',
            value: paymentTotal,
          },
          ...purchaseDetails(),
        });
      } catch (_) {
        // Optional analytics must never prevent acknowledgement or instructions.
      }
      toast({ title: 'העבר כספים ל-Bit' });
      // show phone for payment in a toast plus more persistent suggestion
      toast({ title: 'Bit / Paybox', description: 'העבירו ל: 054-696-9974. שלחו צילום אישור תשלום ל-info@printeam.co.il' });
      // Backend removed: no background forward
      // navigate to thank-you page so the user sees confirmation/next steps
      try {
        navigate('/thank-you', {
          state: { capturedPaymentContext },
        });
      } catch (e) { /* ignore */ }
      setProcessing(false);
      onClose();
      return;
    }

    } catch (error) {
      try { record('checkout_processing_failed', { code: error?.code || 'checkout_processing_failed' }); } catch (_) {}
      toast({ title: 'התשלום לא נפתח', variant: 'destructive',
        description: 'לא הצלחנו להשלים את הבקשה. נסו שוב או פנו אלינו.' });
    } finally { confirmInFlightRef.current = false; setProcessing(false); }
  };

  // Refs for method buttons to support keyboard navigation
  const methodRefs = useRef([]);

  const checkoutReadiness = evaluateCheckoutReadiness({
    processing,
    name,
    phone,
    email,
    withDelivery: payload?.withDelivery === true,
    street: payload?.contact?.street || payload?.contact?.address_street || '',
    city: payload?.contact?.city || payload?.contact?.address_city || '',
    agreedToTerms,
  });
  const checkoutReady = checkoutReadiness.ready && checkoutPricing.ok !== false;
  const checkoutReadinessMessage = checkoutPricing.ok === false
    ? (language === 'he' ? 'לא ניתן לחשב את מחיר ההזמנה. יש לסגור את החלון ולערוך את הפריט בעגלה.' : 'The order price could not be calculated. Close this dialog and edit the cart item.')
    : getCheckoutReadinessMessage(checkoutReadiness, language);

  if (!open) return null;

  const onMethodKeyDown = (e) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const idx = methodCards.findIndex((m) => m.id === method);
    if (idx === -1) return;
    let next = idx;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (idx - 1 + methodCards.length) % methodCards.length;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (idx + 1) % methodCards.length;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = methodCards.length - 1;
    const nextId = methodCards[next].id;
    handlePaymentMethodChange(nextId);
    // focus the new option
    const node = methodRefs.current[next];
    if (node && node.focus) node.focus();
  };

  return createPortal(
    <div className="fixed inset-0 z-[2200]">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative flex min-h-full items-start justify-center overflow-y-auto p-3 pt-20 sm:items-center sm:p-6">
        <motion.div
          id="checkout-payment-dialog"
          initial={{ opacity: 0, scale: 0.98, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.22 }}
          className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-black/5 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="checkout-modal-title"
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <h2 id="checkout-modal-title" className="text-lg font-bold">אמצעי תשלום</h2>
            <button
              type="button"
              aria-label="סגור"
              onClick={onClose}
              className="ml-3 inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 active:bg-gray-200"
            >
              ✕
            </button>
            {checkoutPricing.ok !== false && <div className="text-sm text-gray-500">סה״כ כולל מע״מ: {totalWithVat}</div>}
          </div>

          {checkoutPricing.ok !== false && <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 space-y-2">
            {checkoutPricing.couponApplied && (
              <div className="flex items-center justify-between text-green-700">
                <span>{language === 'he' ? 'קופון' : 'Coupon'} {checkoutPricing.coupon.code}</span>
                <span>{language === 'he' ? 'הוחל' : 'Applied'}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span>{language === 'he' ? 'סה״כ כולל מע״מ' : 'Total incl. VAT'}</span>
              <span>{formatCurrency(baseTotal)}</span>
            </div>
            {cardDiscount > 0 && (
              <div className="flex items-center justify-between text-green-600 font-medium">
                <span>{t('cardPaymentDiscount')}</span>
                <span>-{formatCurrency(cardDiscount)}</span>
              </div>
            )}
            {couponDiscount > 0 && (
              <div className="flex items-center justify-between text-green-600 font-medium">
                <span>{language === 'he' ? 'קופון test99 (99%)' : 'Coupon test99 (99%)'}</span>
                <span>-{formatCurrency(couponDiscount)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-base font-semibold">
              <span>{cardDiscount > 0 || couponDiscount > 0 ? t('totalDue') : (language === 'he' ? 'סה״כ כולל מע״מ' : 'Total incl. VAT')}</span>
              <span>{totalWithVat}</span>
            </div>
          </div>}

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div role="radiogroup" aria-label="Payment methods" onKeyDown={onMethodKeyDown} className="col-span-2 grid grid-cols-2 gap-3">
              {methodCards.map(({ id, label, icon: Icon }, i) => (
                <motion.button
                  key={id}
                  type="button"
                  ref={(el) => { methodRefs.current[i] = el; }}
                  role="radio"
                  aria-checked={method === id}
                  tabIndex={method === id ? 0 : -1}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handlePaymentMethodChange(id)}
                  className={`flex min-h-14 items-center gap-3 rounded-lg border p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${method === id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                >
                  <div className={`p-2 rounded-md ${method === id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'}`}>
                    <Icon size={18} />
                  </div>
                  <div className="text-right flex-1">
                    {id === 'card' ? (
                      <>
                        <div className="font-medium">כרטיס אשראי</div>
                        {checkoutPricing.coupon.type !== 'fixed_unit_gross' && <div className="text-xs text-green-600 font-medium">(הנחה נוספת 3%)</div>}
                      </>
                    ) : (
                      <div className="font-medium">{label}</div>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm">
              <span>שם מלא <span className="text-red-500">*</span></span>
              <input
                required
                aria-required
                autoComplete="name"
                className="w-full border rounded-md px-3 py-2 text-sm mt-1"
                value={name}
                onChange={(e) => { setName(e.target.value); try { mergePayload({ contact: { ...(payload?.contact || {}), name: e.target.value } }); } catch (er) {} }}
                placeholder="שם מלא"
              />
            </label>

            <label className="block text-sm">
              <span>טלפון <span className="text-red-500">*</span></span>
              <input
                required
                aria-required
                autoComplete="tel"
                className="w-full border rounded-md px-3 py-2 text-sm mt-1"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); try { mergePayload({ contact: { ...(payload?.contact || {}), phone: e.target.value } }); } catch (er) {} }}
                placeholder="טלפון"
              />
            </label>

            <label className="block text-sm">
              <span>אימייל <span className="text-red-500">*</span></span>
              <input
                required
                aria-required
                autoComplete="email"
                type="email"
                className="w-full border rounded-md px-3 py-2 text-sm mt-1"
                value={email}
                onChange={(e) => { setEmail(e.target.value); try { mergePayload({ contact: { ...(payload?.contact || {}), email: e.target.value } }); } catch (er) {} }}
                placeholder="אימייל"
              />
            </label>
          </div>

          {method === 'card' && cardDiscount > 0 && (
            <div className="text-sm text-green-600 font-medium text-center mt-3">
              חסכת {formatCurrency(cardDiscount)} בבחירת תשלום בכרטיס אשראי
            </div>
          )}

          <label className="flex items-start gap-2 mt-4 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 flex-shrink-0 accent-blue-600"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
            />
            <span className="text-xs text-gray-600 leading-relaxed">
              קראתי ואני מסכים/ה ל<a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">תנאי השימוש</a>,
              {' '}<a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">מדיניות הפרטיות</a>
              {' '}ו<a href="/returns" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">מדיניות הביטולים</a> של האתר.
            </span>
          </label>

          <p
            id="checkout-readiness-message"
            role="status"
            aria-live="polite"
            className={`mt-3 text-sm ${checkoutReady ? 'font-medium text-green-700' : 'text-gray-600'}`}
          >
            {checkoutReadinessMessage}
          </p>

          <div className="flex items-center justify-between gap-3 mt-3">
            <Button
              type="button"
              onClick={handleConfirm}
              className="flex-1 gap-2 active:scale-[0.99] motion-reduce:transform-none"
              disabled={!checkoutReady}
              aria-describedby="checkout-readiness-message"
              aria-busy={processing}
            >
              {processing ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white motion-reduce:animate-none" aria-hidden="true" />
                  {language === 'he' ? 'מעבד…' : 'Processing…'}
                </>
              ) : method === 'card' ? 'המשך לתשלום מאובטח בכרטיס אשראי' : method === 'bit' ? 'השלם תשלום ב-Bit' : 'שלח פרטי יצירת קשר'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="active:translate-y-px motion-reduce:transform-none">ביטול</Button>
          </div>
        </motion.div>
      </div>
      {processing && (
        <div className="absolute inset-0 z-70 flex items-center justify-center" role="status" aria-live="assertive">
          <div className="bg-black/40 absolute inset-0" />
          <div className="relative z-80 flex items-center gap-4 bg-white/95 rounded-lg p-4 shadow">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <div className="text-sm font-medium">{processingMessage}</div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
